import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';

import { WinPopup } from "./WinPopup";
import { useSelector } from 'react-redux';
import { UIRootState } from 'ui/store/reducers';
import { Popups, WinPopupData } from 'ui/types/popups';

import './popups-common.css';
import { WhatsNew } from "./WhatsNew";
import { useEffect, useRef } from "react";
import { GameUI } from "game/gameUI/GameUI";
import { markBannerShown, shouldShowWhatsNewPopup } from "./utils";
import { useScript } from "ui/hooks/useScript";
import { NotificationPopup } from "./NotificationPopup";
import { FeedBack } from "./FeedBack";
import { FacebookPopup } from "./FacebookPopup";
import { MagicWandPopup } from "./MagicWandPopup";
import { OutMagicWandPopup } from "./OutMagicWandPopup";
import { OutMoveWandPopup } from "./OutMoveWandPopup";
import { OutMoveAdsPopup } from "./OutMoveAdsPopup";
import { OutUndoPopup } from "./OutUndoPopup";
import { KingPopup } from "./KingPopup";
import { StoriesNoTicketsPopup } from "./StoriesNoTicketsPopup";
import { NoAdsPopup } from "./NoAdsPopup";
import { StoriesCongratulationsPopup } from "./StoriesCongratulationsPopup";
import { StoriesUseTicketsPopup } from "./StoriesUseTicketsPopup";
import { AdblockPopup } from "./AdblockPopup";
import { TutorialStartPopup } from "./TutorialStartPopup";
import { TutorialCompletedPopup } from "./TutorialCompletedPopup";
import { TutorialRestartPopup } from "./TutorialRestartPopup";
import { TutorialSkipPopup } from "./TutorialSkipPopup";
import { TutorialCompletedSpiderPopup } from "./TutorialCompletedSpiderPopup";
import { NoHintsPopup } from "./NoHintsPopup";
import { Store } from "game/store/gameStore";

const BANNER_KEY = "whats_new_state";
export const BANNER_VERSION = "1.3.9";
const MIN_WINS = 5;

export const PopupsUI = () => {
    const popupContainerRef = useRef<HTMLDivElement>(null);
    const winPopup = useSelector((state: UIRootState) => state.popups?.[Popups.WIN_POPUP]);
    const whatsNewPopup = useSelector((state: UIRootState) => state.popups?.[Popups.WHATS_NEW]);
    const notificationPopup = useSelector((state: UIRootState) => state.popups?.[Popups.NOTIFICATION_POPUP]);
    const feedbackPopup = useSelector((state: UIRootState) => state.popups?.[Popups.FEEDBACK_POPUP]);
    const facebookPopup = useSelector((state: UIRootState) => state.popups?.[Popups.FACEBOOK_POPUP]);
    const magicWandPopup = useSelector((state: UIRootState) => state.popups?.[Popups.MAGIC_WAND_POPUP]);
    const outMagicWandPopup = useSelector((state: UIRootState) => state.popups?.[Popups.OUT_MAGIC_WAND_POPUP]);
    const outMoveWandPopup = useSelector((state: UIRootState) => state.popups?.[Popups.OUT_MOVE_WAND_POPUP]);
    const outMoveAdsPopup = useSelector((state: UIRootState) => state.popups?.[Popups.OUT_MOVE_ADS_POPUP]);
    const outUndoPopup = useSelector((state: UIRootState) => state.popups?.[Popups.OUT_UNDO_POPUP]);
    const storiesNoTicketsPopup = useSelector((state: UIRootState) => state.popups?.[Popups.STORIES_NO_TICKETS_POPUP]);
    const noAdsPopup = useSelector((state: UIRootState) => state.popups?.[Popups.NO_ADS_POPUP]);
    const storiesCongratulationsPopup = useSelector((state: UIRootState) => state.popups?.[Popups.STORIES_CONGRATULATIONS_POPUP]);
    const storiesUseTicketsPopup = useSelector((state: UIRootState) => state.popups?.[Popups.STORIES_USE_TICKETS_POPUP]);
    const adblockerPopup = useSelector((state: UIRootState) => state.popups?.[Popups.ADBLOCKER_POPUP]);
    const tutorialStartPopup = useSelector((state: UIRootState) => state.popups?.[Popups.TUTORIAL_START]);
    const tutorialCompletedPopup = useSelector((state: UIRootState) => state.popups?.[Popups.TUTORIAL_COMPLETED]);
    const tutorialRestartPopup = useSelector((state: UIRootState) => state.popups?.[Popups.TUTORIAL_RESTART]);
    const tutorialSkipPopup = useSelector((state: UIRootState) => state.popups?.[Popups.TUTORIAL_SKIP]);
    const tutorialCompletedSpiderPopup = useSelector((state: UIRootState) => state.popups?.[Popups.TUTORIAL_COMPLETED_SPIDER]);
    const noHintsPopup = useSelector((state: UIRootState) => state.popups?.[Popups.NO_HINTS_POPUP]);
    const isAnyPopupOpen = useSelector((state: UIRootState) => Object.values(state.popups ?? {}).some(popup => popup?.isOpen && !(popup.data as any)?.isNotification));

    useScript('https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js');
    useScript('/assets/js/notifications.js');

    useEffect(() => {
        setTimeout(() => {
            if (!Store.tutorialActive && shouldShowWhatsNewPopup(BANNER_KEY, BANNER_VERSION, MIN_WINS)) {
                GameUI.showPopup(Popups.WHATS_NEW);
                (window as any)?.dataLayer?.push({
                    event: "whats_new_banner_view",
                    version: BANNER_VERSION,
                });
                markBannerShown(BANNER_KEY, BANNER_VERSION);
            }
        }, 1000);
    }, []);

    useEffect(() => {
        if (isAnyPopupOpen) {
            if (Store.inArticle) {
                const container = document.getElementById('solitaire-container');
                container?.scrollIntoView({ behavior: 'instant', block: 'center' });
            } else {
                window.scrollTo(0, 0);
            }

            document.body.style.overflow = 'hidden';
            if (popupContainerRef.current) {
                disableBodyScroll(popupContainerRef.current);
            }
        } else {
            document.body.style.overflow = 'auto';
            clearAllBodyScrollLocks();
        }
    }, [isAnyPopupOpen]);

    return (
        <>
            <KingPopup />
            {isAnyPopupOpen && (
                <div ref={popupContainerRef} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 10000 }}>
                    <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0, 0, 0, 0.4)' }}/>
                    {winPopup?.isOpen && <WinPopup data={winPopup.data as WinPopupData} />}
                    {whatsNewPopup?.isOpen && <WhatsNew />}
                    {notificationPopup?.isOpen && <NotificationPopup />}
                    {feedbackPopup?.isOpen && <FeedBack />}
                    {facebookPopup?.isOpen && <FacebookPopup />}
                    {magicWandPopup?.isOpen && <MagicWandPopup />}
                    {outMagicWandPopup?.isOpen && <OutMagicWandPopup />}
                    {outMoveWandPopup?.isOpen && <OutMoveWandPopup />}
                    {outMoveAdsPopup?.isOpen && <OutMoveAdsPopup />}
                    {outUndoPopup?.isOpen && <OutUndoPopup />}
                    {storiesNoTicketsPopup?.isOpen && <StoriesNoTicketsPopup />}
                    {noAdsPopup?.isOpen && <NoAdsPopup />}
                    {storiesCongratulationsPopup?.isOpen && <StoriesCongratulationsPopup />}
                    {storiesUseTicketsPopup?.isOpen && <StoriesUseTicketsPopup />}
                    {adblockerPopup?.isOpen && <AdblockPopup />}
                    {tutorialStartPopup?.isOpen && <TutorialStartPopup />}
                    {tutorialCompletedPopup?.isOpen && <TutorialCompletedPopup />}
                    {tutorialRestartPopup?.isOpen && <TutorialRestartPopup />}
                    {tutorialSkipPopup?.isOpen && <TutorialSkipPopup />}
                    {tutorialCompletedSpiderPopup?.isOpen && <TutorialCompletedSpiderPopup />}
                    {noHintsPopup?.isOpen && <NoHintsPopup />}
                </div>
            )}
        </>
    );
};